import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './TitleBlock.module.css';
import type { HeaderHover, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface TitleBlockProps {
  title: string;
  /**
   * Drawn inside the resting button, ahead of the name — the brand kit workspace's own mark, so
   * the title reads `[mark] Acme ⌄` and the kit is identified where its name already is (Guri,
   * 2026-09-03). Sized by the caller: the block is a fixed 24px, so 18-20px fits.
   *
   * It is NOT rendered while editing, and that is deliberate rather than missed — see the comment
   * over the editing branch's return.
   */
  leading?: React.ReactNode;
  /** Editing is controlled so the header (and its stories) can pin state S3 without a real dblclick. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Enter commits. Escape reverts and never calls this. An unchanged or empty name never commits. */
  onRename?: (next: string) => void;
  /**
   * Opens the deck menu. Must TOGGLE, not just open: the block is one button so that a click
   * anywhere on the name works, which means a double-click fires two clicks before `dblclick`.
   * A toggle lands back on "closed" and the rename takes over cleanly; a plain open would leave
   * the menu hanging over the field.
   */
  onCaretClick?: () => void;
  /**
   * That menu is open right now. Drawn nowhere — it is `aria-expanded`, and it is the half of the
   * trigger's contract a sighted user reads off the menu itself.
   */
  expanded?: boolean;
  /**
   * What the button announces, when the name alone is not the whole answer.
   *
   * Left undefined the accessible name is the deck's name, which is right for a menu of things you
   * do TO this deck. The brand kit workspace is the other case: its caret opens a list of OTHER
   * kits, so the button there says `Switch brand kit, currently Northwind` — the exact sentence the
   * separate switcher trigger carried before it was folded into this one (Guri, 2026-09-03). Losing
   * it in the fold would have been the whole point of the fold undone for anyone not looking at it.
   */
  menuLabel?: string;
  /**
   * What the in-place field is editing. `Deck name` unless the host says otherwise — this component
   * renames brand kits too now, and telling someone they are editing a deck's name while they are
   * renaming a kit is the one string that cannot be left generic.
   */
  nameLabel?: string;
  theme?: HeaderTheme;
  hover?: HeaderHover;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * `logo · title ⌄` — the deck's name, 24px tall on the 40px bar, with the caret that opens the deck
 * menu. Double-clicking the label renames in place: enter saves, escape reverts, no pencil icon.
 */
export function TitleBlock({
  title,
  leading,
  editing = false,
  onEditingChange,
  onRename,
  onCaretClick,
  expanded = false,
  menuLabel,
  nameLabel = 'Deck name',
  theme: themeProp,
  hover = 'shape',
  forceHover = false,
  className,
}: TitleBlockProps) {
  const theme = useV15Theme(themeProp);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const sizerRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  /** The field's width in px, measured off the sizer. `null` until the first measurement. */
  const [fieldWidth, setFieldWidth] = useState<number | null>(null);

  // Entering edit mode re-seeds the draft from the committed title, so a previous escape can never
  // leak into the next rename.
  useEffect(() => {
    if (editing) {
      setDraft(title);
    }
  }, [editing, title]);

  useEffect(() => {
    if (!editing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // `select()` leaves the selection's focus end at the LAST character, and the browser then
    // scrolls there to keep it in view — so double-clicking a name longer than the field swaps its
    // first words for its last ones. Selecting backward puts the focus end at character 0 and the
    // field stays where the label was. Same selection either way, and the box never moves; without
    // this the text inside it does. `scrollLeft` alone does not hold — the browser re-scrolls to
    // the focus end on the next paint.
    input.setSelectionRange(0, input.value.length, 'backward');
    // Chrome scrolls to the selection's end regardless of direction, so pin it back. This only
    // works because the input is now the scroller — while the sizer shared a grid cell with it the
    // input was as wide as the text and it was the WRAPPER that scrolled, which no amount of
    // setting `input.scrollLeft` could reach.
    input.scrollLeft = 0;
  }, [editing]);

  // Measured in a LAYOUT effect, not an ordinary one, and that is the whole trick: React has already
  // committed the new text to the input, and this runs before the browser paints, so the box and the
  // text it is sized to reach the screen in the same frame. A frame of lag here is a frame in which
  // the text is wider than its own scroller, which a focused input answers by scrolling — and the
  // name visibly slides. The CSS has the rest of that story; the short version is that nothing about
  // this width may be deferred or animated.
  //
  // The sizer carries the input's own `0 7px`, so its width IS the field's width — no lane to add.
  //
  // Clamped to the box's own computed ceiling rather than a number repeated here: `max-width` would
  // clamp the painted result anyway, but reporting an unclamped target to a box that cannot honour
  // it means the two disagree about how wide the field is.
  useLayoutEffect(() => {
    if (!editing) {
      setFieldWidth(null);
      return;
    }
    const sizer = sizerRef.current;
    if (!sizer) return;
    const natural = sizer.getBoundingClientRect().width;
    const ceiling = wrapRef.current ? parseFloat(getComputedStyle(wrapRef.current).maxWidth) : NaN;
    setFieldWidth(Number.isFinite(ceiling) ? Math.min(natural, ceiling) : natural);
  }, [editing, draft]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== title) onRename?.(next);
    onEditingChange?.(false);
  };

  if (editing) {
    return (
      // The field grows with the name instead of jumping to a fixed width. `ch` units overshoot
      // badly on a semibold display face, so the width comes from a hidden copy of the text laid
      // out in the same type, with both stacked in one grid cell.
      //
      // `leading` is not drawn here, on purpose. This box is a measured box: the sizer, the input's
      // `0 7px` and the width read back in the layout effect above all describe THE NAME and
      // nothing else, and a mark inside it would be width the ruler never measured — the field
      // would come out narrower than its own text, which is the exact failure the whole comment
      // block above this function is about. Sizing it in would mean the mark's width has to be
      // known in a layout effect too, and the name would then move on the frame the mark loaded.
      // So the mark simply is not there while you type, which is already what happens to the caret
      // — edit mode is the name alone on a field, and it goes back to `[mark] name ⌄` on commit.
      <span
        ref={wrapRef}
        className={cn(themes[theme], styles.fieldWrap, className)}
        style={
          fieldWidth === null
            ? undefined
            : ({ '--hv15-field-w': `${fieldWidth}px` } as React.CSSProperties)
        }
      >
        <span className={styles.sizer} ref={sizerRef} aria-hidden>
          {draft || ' '}
        </span>
        <input
        ref={inputRef}
        className={styles.field}
        // Without this the input contributes its own 20-character intrinsic width to the grid cell
        // and the sizer stops mattering. See `.field` in the stylesheet.
        size={1}
        value={draft}
        aria-label={nameLabel}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(title);
            onEditingChange?.(false);
          }
        }}
        // Clicking away is a save, matching every other rename-in-place field in the product.
        onBlur={commit}
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        themes[theme],
        styles.block,
        hover === 'tone' && styles.tone,
        hover === 'shape' && styles.shape,
        forceHover && styles.forceHover,
        className,
      )}
      onClick={onCaretClick}
      /* The block IS the trigger — the caret is a glyph inside it, not a second button — so the
         menu contract goes here. Both are conditional on there being a menu at all: a title with no
         `onCaretClick` is a name you can rename, and announcing a popup on it would be a promise
         the click does not keep. */
      aria-haspopup={onCaretClick ? 'menu' : undefined}
      aria-expanded={onCaretClick ? expanded : undefined}
      aria-label={menuLabel}
      onDoubleClick={(e) => {
        e.preventDefault();
        onEditingChange?.(true);
      }}
    >
      {leading ? <span className={styles.leading}>{leading}</span> : null}
      <span className={styles.label}>{title}</span>
      <span className={styles.caret}>
        <DeckIcon name="arrow-down-s" size={18} />
      </span>
    </button>
  );
}
